import type {
  ApiEnvelope,
  AttendancePoint,
  AttendanceSession,
  ClientDetailRecord,
  ClientRecord,
  HomeDashboardData,
  LoginChallengeResponse,
  LoginDevicePayload,
  LoginSessionResponse,
  PasswordChangeResponse,
  ServiceCatalogEntry,
  ServiceDraft,
  ServiceUpdate,
  SessionRecord,
  StaffApiPage,
  StaffApiUser,
  StaffPerformanceRecord,
  UserRecord,
} from './types';
import { readJsonStorage, storageKeys } from './storage';

export class ApiRequestError extends Error {
  readonly statusCode: number;
  readonly details: unknown;

  constructor(message: string, statusCode: number, details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.statusCode = statusCode;
    this.details = details;
  }
}

const DEFAULT_API_BASE_URL =
  import.meta.env.DEV === true ? 'http://localhost:4000/api/v1' : '/api/v1';

function getEnvApiBaseUrl() {
  return (
    String(import.meta.env.VITE_API_URL_WEB ?? import.meta.env.VITE_API_URL ?? '').trim() || null
  );
}

export function getApiBaseUrl() {
  return getEnvApiBaseUrl() || DEFAULT_API_BASE_URL;
}

function buildUrl(path: string, query?: Record<string, string | number | null | undefined>) {
  const baseUrl = getApiBaseUrl();
  const nextPath = path.startsWith('/') ? path : `/${path}`;
  const searchParams = new URLSearchParams();

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      searchParams.set(key, String(value));
    });
  }

  const queryString = searchParams.toString();

  return `${baseUrl}${nextPath}${queryString ? `?${queryString}` : ''}`;
}

function getSessionAuthorizationHeader(): Record<string, string> {
  const session = readJsonStorage<Partial<SessionRecord>>(storageKeys.session);

  return typeof session?.token === 'string' && session.token.trim()
    ? { Authorization: `Bearer ${session.token}` }
    : {};
}

async function readJsonResponse<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

async function requestJson<TResponse>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: Record<string, unknown>,
  query?: Record<string, string | number | null | undefined>,
  signal?: AbortSignal,
  headers?: Record<string, string>
) {
  let response: Response;

  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers: {
        ...getSessionAuthorizationHeader(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch {
    throw new Error(`Unable to reach the backend at ${getApiBaseUrl()}. Check your network and API URL.`);
  }

  const payload = (await readJsonResponse<ApiEnvelope<TResponse> & { error?: { details?: unknown } }>(
    response
  )) as (ApiEnvelope<TResponse> & { error?: { details?: unknown } }) | null;

  if (!response.ok) {
    const message =
      payload && typeof payload.message === 'string' && payload.message.trim()
        ? payload.message
        : `Request failed with status ${response.status}.`;

    throw new ApiRequestError(message, response.status, payload?.error?.details);
  }

  if (!payload || payload.data === undefined || payload.data === null) {
    throw new Error('The server returned an unexpected response.');
  }

  return payload.data;
}

async function getJson<T>(
  path: string,
  query?: Record<string, string | number | null | undefined>,
  signal?: AbortSignal,
  headers?: Record<string, string>
) {
  let response: Response;

  try {
    response = await fetch(buildUrl(path, query), {
      method: 'GET',
      headers: {
        ...getSessionAuthorizationHeader(),
        ...headers,
      },
      signal,
    });
  } catch {
    throw new Error(`Unable to reach the backend at ${getApiBaseUrl()}. Check your network and API URL.`);
  }

  const payload = (await readJsonResponse<ApiEnvelope<T> & { error?: { details?: unknown } }>(
    response
  )) as (ApiEnvelope<T> & { error?: { details?: unknown } }) | null;

  if (!response.ok) {
    const message =
      payload && typeof payload.message === 'string' && payload.message.trim()
        ? payload.message
        : `Request failed with status ${response.status}.`;

    throw new ApiRequestError(message, response.status, payload?.error?.details);
  }

  if (!payload || payload.data === undefined || payload.data === null) {
    throw new Error('The server returned an unexpected response.');
  }

  return payload.data;
}

export async function fetchIntroContent(signal?: AbortSignal) {
  return getJson<{
    appName: string;
    heroTitle: string;
    heroSubtitle: string;
    slides: Array<{ id: string; title: string; description: string }>;
    highlights: string[];
    primaryAction: { label: string; target: string };
    secondaryAction: { label: string; target: string };
  }>('/content/intro', undefined, signal);
}

export function getLoginDevicePayload(): LoginDevicePayload {
  const deviceIdKey = '@slotlooks/device-id';
  let deviceId = '';

  try {
    deviceId = window.localStorage.getItem(deviceIdKey) || '';
  } catch {
    deviceId = '';
  }

  if (!deviceId) {
    deviceId = `device-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

    try {
      window.localStorage.setItem(deviceIdKey, deviceId);
    } catch {
      // Ignore persistence failures.
    }
  }

  return {
    device_token: '',
    device_id: deviceId,
    type: 'web',
  };
}

export function requestLoginChallenge(
  body: {
    type: 'otp';
    phoneNumber: string;
    firebaseToken?: string;
    device?: LoginDevicePayload;
  },
  signal?: AbortSignal
) {
  return requestJson<LoginChallengeResponse>('POST', '/auth/login', body as Record<string, unknown>, undefined, signal);
}

export function loginWithUsernamePassword(
  body: {
    type: 'username';
    username: string;
    password: string;
    firebaseToken?: string;
    device?: LoginDevicePayload;
  },
  signal?: AbortSignal
) {
  return requestJson<LoginSessionResponse>('POST', '/auth/login', body as Record<string, unknown>, undefined, signal);
}

export function verifyLoginOtp(
  body: {
    challengeId: string;
    otp: string;
    device?: LoginDevicePayload;
  },
  signal?: AbortSignal
) {
  return requestJson<LoginSessionResponse>('POST', '/auth/verify-otp', body, undefined, signal);
}

export function changeCurrentPassword(
  sessionToken: string,
  body: {
    currentPassword: string;
    newPassword: string;
  },
  signal?: AbortSignal
) {
  return requestJson<PasswordChangeResponse>(
    'PATCH',
    '/users/me/password',
    body as Record<string, unknown>,
    undefined,
    signal,
    {
      Authorization: `Bearer ${sessionToken}`,
    }
  );
}

export async function fetchHomeDashboardData(signal?: AbortSignal) {
  return getJson<HomeDashboardData>('/home', undefined, signal);
}

export async function fetchClients(signal?: AbortSignal) {
  return getJson<{ items: ClientRecord[]; count: number }>('/clients', undefined, signal);
}

export async function fetchClient(clientKey: string, signal?: AbortSignal) {
  return getJson<ClientDetailRecord>(`/clients/${clientKey}`, undefined, signal);
}

export async function fetchServices(signal?: AbortSignal) {
  const payload = await getJson<{ items: ServiceCatalogEntry[]; count: number }>('/services', undefined, signal);
  return payload.items;
}

export async function createService(body: ServiceDraft, signal?: AbortSignal) {
  return requestJson<ServiceCatalogEntry>('POST', '/services', body as Record<string, unknown>, undefined, signal);
}

export async function updateService(
  serviceId: string,
  body: ServiceUpdate,
  signal?: AbortSignal
) {
  return requestJson<ServiceCatalogEntry>(
    'PATCH',
    `/services/${serviceId}`,
    body as Record<string, unknown>,
    undefined,
    signal
  );
}

export async function deleteService(serviceId: string, signal?: AbortSignal) {
  return requestJson<{ deleted: boolean }>('DELETE', `/services/${serviceId}`, undefined, undefined, signal);
}

export async function fetchStaffPage({
  page = 1,
  limit = 20,
  role = 'staff',
  signal,
}: {
  page?: number;
  limit?: number;
  role?: 'admin' | 'staff';
  signal?: AbortSignal;
}) {
  return getJson<StaffApiPage>('/users', { page, limit, role }, signal);
}

export async function fetchAllStaffUsers({
  limit = 20,
  role = 'staff',
  signal,
}: {
  limit?: number;
  role?: 'admin' | 'staff';
  signal?: AbortSignal;
} = {}) {
  const items: StaffApiUser[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const response = await fetchStaffPage({ page, limit, role, signal });
    items.push(...response.items);
    totalPages = response.totalPages || (response.hasMore ? page + 1 : page);

    if (!response.hasMore) {
      break;
    }

    page += 1;
  }

  return items;
}

export async function fetchStaffPerformance(signal?: AbortSignal) {
  const payload = await fetchHomeDashboardData(signal);
  return Array.isArray(payload.staff_performance) ? payload.staff_performance : [];
}

function toIsoDate(value: unknown) {
  if (typeof value === 'string') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return String(value ?? '');
}

function normalizeAttendanceSession(value: Record<string, unknown>): AttendanceSession {
  const clockInLocation = (value.clockInLocation ?? {}) as Record<string, unknown>;
  const clockOutLocation = value.clockOutLocation as Record<string, unknown> | null | undefined;

  return {
    id: String(value.id ?? ''),
    staffId: String(value.staffId ?? ''),
    staffName: String(value.staffName ?? ''),
    staffRole: String(value.staffRole ?? ''),
    clockInAt: toIsoDate(value.clockInAt),
    clockInLocation: {
      latitude: Number(clockInLocation.latitude ?? 0),
      longitude: Number(clockInLocation.longitude ?? 0),
      accuracy: typeof clockInLocation.accuracy === 'number' ? clockInLocation.accuracy : null,
    },
    clockOutAt: value.clockOutAt ? toIsoDate(value.clockOutAt) : null,
    clockOutLocation: clockOutLocation
      ? {
          latitude: Number(clockOutLocation.latitude ?? 0),
          longitude: Number(clockOutLocation.longitude ?? 0),
          accuracy: typeof clockOutLocation.accuracy === 'number' ? clockOutLocation.accuracy : null,
        }
      : null,
  };
}

export async function fetchAttendanceSessions(sessionToken: string, signal?: AbortSignal) {
  const payload = await getJson<{ items: Array<Record<string, unknown>>; count: number }>(
    '/attendance',
    undefined,
    signal,
    {
      Authorization: `Bearer ${sessionToken}`,
    }
  );

  return payload.items.map(normalizeAttendanceSession);
}

export async function fetchMyAttendanceSessions(sessionToken: string, signal?: AbortSignal) {
  const payload = await getJson<{ items: Array<Record<string, unknown>>; count: number }>(
    '/attendance/me',
    undefined,
    signal,
    {
      Authorization: `Bearer ${sessionToken}`,
    }
  );

  return payload.items.map(normalizeAttendanceSession);
}

export async function clockInAttendance(sessionToken: string, location: AttendancePoint, signal?: AbortSignal) {
  const payload = await requestJson<Record<string, unknown>>(
    'POST',
    '/attendance/clock-in',
    location as unknown as Record<string, unknown>,
    undefined,
    signal,
    {
      Authorization: `Bearer ${sessionToken}`,
    }
  );

  return normalizeAttendanceSession(payload);
}

export async function clockOutAttendance(sessionToken: string, location: AttendancePoint, signal?: AbortSignal) {
  const payload = await requestJson<Record<string, unknown>>(
    'POST',
    '/attendance/clock-out',
    location as unknown as Record<string, unknown>,
    undefined,
    signal,
    {
      Authorization: `Bearer ${sessionToken}`,
    }
  );

  return normalizeAttendanceSession(payload);
}

export async function createStaffUser(body: {
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
  role?: 'admin' | 'staff';
  username?: string;
}) {
  return requestJson<StaffApiUser>('POST', '/users', {
    ...body,
    role: body.role ?? 'staff',
  });
}

export async function updateStaffUser(
  staffId: string,
  body: {
    name?: string;
    email?: string;
    phoneNumber?: string;
    password?: string;
    role?: 'admin' | 'staff';
    username?: string;
  }
) {
  return requestJson<StaffApiUser>('PATCH', `/users/${staffId}`, body);
}

export async function deleteStaffUser(staffId: string) {
  return requestJson<{ deleted: boolean }>('DELETE', `/users/${staffId}`);
}

// ─── Appointment Feedback ────────────────────────────────────────────────────

export type FeedbackFormData = {
  appointment: {
    id: string;
    date: string;
    services: string[];
    status: string;
    feedback_flag: boolean;
    feedback_submitted_at: string | null;
  };
  client: {
    id: string;
    name: string;
  } | null;
  feedback: {
    id: string;
    appointment_id: string;
    feedback: string;
    rating: number;
    ratings: {
      service_quality: number;
      staff_behavior: number;
      cleanliness: number;
      waiting_time: number;
      value_for_money: number;
    } | null;
    services: string[];
    recommend: number | null;
    liked_most: string;
    comment: string;
  } | null;
};

export type FeedbackSubmission = {
  feedback: string;
  rating: number;
  ratings?: {
    service_quality: number;
    staff_behavior: number;
    cleanliness: number;
    waiting_time: number;
    value_for_money: number;
  };
  services?: string[];
  recommend?: number | null;
  liked_most?: string;
  comment?: string;
};

export async function fetchFeedbackForm(token: string, signal?: AbortSignal) {
  return getJson<FeedbackFormData>(`/appointment-feedback/forms/${token}`, undefined, signal);
}

export async function submitFeedbackForm(token: string, body: FeedbackSubmission, signal?: AbortSignal) {
  return requestJson<FeedbackSubmission>('POST', `/appointment-feedback/forms/${token}`, body as Record<string, unknown>, undefined, signal);
}
